import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { MatSortDirection, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IQuizListItem } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { FiQuizService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz.service';
import { FiQuizActionsBuilderService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-actions-builder.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';

@Component({
    selector: 'fi-quiz-list',
    templateUrl: './fi-quiz-list.component.html',
    styleUrl: './fi-quiz-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-quiz-list' },
    standalone: false,
})
export class FiQuizListComponent extends FiRemoteStateFullDataSource<IQuizListItem> {
    private fiQuizActionsBuilderService = inject(FiQuizActionsBuilderService);
    private fiQuizService = inject(FiQuizService);

    public readonly defaultSortColumnName = 'CreatedOn';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.Quiz;
    public readonly itemsListType = FiItemsListType.QuizListItems;
    public getRowClassFn = (quizListItem: IQuizListItem): string =>
        quizListItem.HasEditAccess ? 'cursor-pointer' : '';

    protected readonly sideListOptions: ISideListOptionsItem[] = [
        { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
    ];
    protected readonly actions: IItemsListAction2<IQuizListItem>[] = this.fiQuizActionsBuilderService.getQuizActions({
        onQuizDeletedFn: (item) => this.removeData([item]),
    });

    protected isEmptyListIsShown = signal(false);

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IQuizListItem>> {
        return new Promise<IItemsListDataFetchResult<IQuizListItem>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiQuizService.getQuizzesListData(parameter).then((data) => {
                if (this.loadingCounter() === 1) {
                    this.isEmptyListIsShown.set(data.Items.length === 0);
                }
                resolve(data);
            }, reject);
        });
    }

    private getParameter(loadArgs: ILoadDataArgs): IItemsListParameterBase {
        const parameter = super.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return parameter;
    }

    protected getItemSharedStatusLabel(isShared: boolean): string {
        return this.appTextService.get(isShared ? 'common.public' : 'task.private');
    }

    protected navigateToQuizViewer(quizListItem: IQuizListItem): void {
        this.fiStateService.go(FiStateNames.Onboarding.Quiz.View, { id: quizListItem.Id });
    }

    protected readonly FiStateNames = FiStateNames;
    protected readonly MatSortDirection = MatSortDirection;
}
